import { IconButton } from "@carbon6/c6-frontend-design";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Keycap, isEditableKeyboardTarget, isHotkeySurfaceActive } from "@/components/keyboard";
import { useLabelCategories } from "@/components/label";
import { EpisodeLabelGuideModal } from "./EpisodeLabelGuideModal";
import { TAG_HOTKEYS } from "./episodeTagHotkeys";
import styles from "./EpisodeLabelGuideAction.module.css";

// 도움말 관례 키. Shift 로만 만들어지는 문자다.
const GUIDE_KEY = "?";

export type EpisodeLabelGuideActionProps = {
  // 서버가 준 어휘·순서. 가이드는 이 배열 위치에서 키캡을 정한다.
  qualityTags: readonly string[];
};

/**
 * 라벨링 가이드 입구. 버튼, `?` 키, 가이드 모달로 이루어진다.
 *
 * 라벨링 영역 머리 줄의 오른쪽 끝에 둔다(`∨ 에피소드 라벨링 [통과] … [? 라벨링 가이드]`).
 * 톤은 상세 헤더의 `Training` 버튼과 같은 `surface`/`md` 다. 키캡 하나만 두면 판정 배지
 * 옆에서 눈에 띄지 않는다.
 *
 * 가이드는 **읽기 동작**이므로 권한·모드와 무관하게 열린다. 뷰 모드에서도, viewer·guest 에게도
 * 열린다. 모달이 열리면 c6 `Modal` 이 뒤를 `inert` 로 잠그므로 독 단축키는 자동으로 멈춘다.
 */
export function EpisodeLabelGuideAction({ qualityTags }: EpisodeLabelGuideActionProps): ReactNode {
  const rootRef = useRef<HTMLDivElement>(null);
  // 열릴 때 마운트하고 닫힘 모션이 끝나면 언마운트한다. 닫혀 있는 동안 표를 그리지 않는다.
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  // 갈래는 가이드가 처음 열릴 때만 조회한다. `?` 를 한 번도 누르지 않은 세션은 요청이 0 이다.
  const categories = useLabelCategories("quality", mounted);

  const openGuide = (): void => {
    setMounted(true);
    setOpen(true);
  };
  const openGuideRef = useRef(openGuide);
  openGuideRef.current = openGuide;

  useEffect(() => {
    function handleKeyDown(event: globalThis.KeyboardEvent): void {
      if (event.defaultPrevented) return;
      // `?` 는 Shift 로만 입력되는 문자다. 앱 단축키 가드(`hasModifierKey`)는 shift 때문에 이 키를
      // 먼저 거른다. 그래서 그 가드를 쓰지 않고 나머지 수식키만 직접 확인한다(Studio 와 같은 이유).
      if (event.key !== GUIDE_KEY) return;
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      if (isEditableKeyboardTarget(event.target)) return;
      if (!isHotkeySurfaceActive(rootRef.current)) return;
      event.preventDefault();
      if (!event.repeat) openGuideRef.current();
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  return (
    <div ref={rootRef} className={styles.root}>
      <IconButton
        text="라벨링 가이드"
        size="md"
        variant="surface"
        title="어휘의 뜻과 단축키를 봅니다. ? 로도 열립니다."
        onClick={openGuide}
      >
        <Keycap>{GUIDE_KEY}</Keycap>
      </IconButton>
      {mounted ? (
        <EpisodeLabelGuideModal
          open={open}
          qualityTags={qualityTags}
          categories={categories}
          tagHotkeys={TAG_HOTKEYS}
          onClose={() => setOpen(false)}
          onClosed={() => setMounted(false)}
        />
      ) : null}
    </div>
  );
}
